import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Camera, Palette, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeGate from "@/components/brand/UpgradeGate";
import ToolShell, { Composer } from "@/components/brand/ToolShell";
import { ChatBubble, UserText, AssistantMarkdown } from "@/components/brand/ChatMarkdown";
import ImageLightbox from "@/components/ImageLightbox";

const TYPES = [
  { value: "Social Media Post", label: "Social Media Post" },
  { value: "Photo", label: "Photo" },
  { value: "Illustration", label: "Illustration" },
  { value: "Other", label: "Other" },
];

const RATIOS = [
  { value: "1:1", label: "1:1" },
  { value: "4:5", label: "4:5" },
  { value: "16:9", label: "16:9 (1920×1080)" },
  { value: "Other", label: "Other" },
];

const QUICK_ACTIONS = [
  { title: "Hero banner", prompt: "Een hero banner voor onze homepage: één centraal product, zachte achtergrond, veel witruimte.", Icon: Camera },
  { title: "Social post", prompt: "Een vierkante social post die onze nieuwste launch aankondigt, in merkkleuren, met ruimte voor tekst links.", Icon: ImageIcon },
  { title: "Pattern / texture", prompt: "Een subtiele achtergrond-texture of pattern dat past bij onze merkidentiteit.", Icon: Palette },
];

export default function BrandCreator({ brand, role }) {
  return (
    <UpgradeGate brand={brand} role={role} feature="The Brand Creator">
      {({ refreshPlan }) => <CreatorInner brand={brand} refreshPlan={refreshPlan} />}
    </UpgradeGate>
  );
}

function CreatorInner({ brand, refreshPlan }) {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [assetType, setAssetType] = useState("");
  const [ratio, setRatio] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lightbox, setLightbox] = useState(null); // {items:[{src,downloadUrl,alt}], index}

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
  }, [brand.id]);

  const parseAssistant = (raw) => {
    if (!raw) return { text: "", images: [] };
    try {
      const obj = JSON.parse(raw);
      return { text: obj.text || "", images: obj.images || [] };
    } catch {
      return { text: raw, images: [] };
    }
  };

  const loadSession = async (sid) => {
    setSessionId(sid);
    setMessages([]);
    try {
      const { data } = await api.get(`/chat/history`, { params: { brand_id: brand.id, tool: "creator", session_id: sid } });
      const parsed = data.map((m, i) => {
        if (m.role === "assistant") {
          const { text, images } = parseAssistant(m.content);
          return { ...m, id: `${sid}_${i}`, content: text, images };
        }
        return { ...m, id: `${sid}_${i}` };
      });
      setMessages(parsed);
    } catch { toast.error("Chat kon niet geladen worden"); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); setInput(""); };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (ratio === "Other" && (!Number(width) || !Number(height))) {
      toast.error("Vul breedte en hoogte in voor custom afmetingen");
      return;
    }
    const activeSid = sessionId || `creator_${brand.id}_${user?.user_id}_${Date.now().toString(36)}`;
    if (!sessionId) setSessionId(activeSid);
    setInput("");
    const uid = Date.now() + "u";
    const aid = Date.now() + "a";
    const displayType = assetType || "—";
    const displayRatio = ratio === "Other" ? `${width}×${height}` : ratio || "—";
    setMessages((m) => [
      ...m,
      { role: "user", content: text, asset_type: displayType, aspect: displayRatio, id: uid },
      { role: "assistant", content: "", images: [], id: aid, streaming: true },
    ]);
    setBusy(true);
    try {
      const body = {
        brand_id: brand.id,
        prompt: text,
        asset_type: assetType || "",
        aspect_ratio: ratio || "",
        session_id: activeSid,
      };
      if (ratio === "Other") {
        body.width = Number(width);
        body.height = Number(height);
      }
      const { data } = await api.post("/chat/creator", body);
      setMessages((m) =>
        m.map((x) =>
          x.id === aid ? { ...x, content: data.text || "", images: data.images || [], streaming: false } : x
        )
      );
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genereren mislukt");
      setMessages((m) => m.filter((x) => x.id !== uid && x.id !== aid));
    } finally {
      setBusy(false);
      refreshPlan?.();
    }
  };

  const firstName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const isEmpty = messages.length === 0;

  const emptyState = (
    <>
      <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 text-center">
        Hi {firstName},
      </h1>
      <p className="mt-3 text-lg text-slate-500 text-center max-w-xl mx-auto">
        I design on-brand visuals for {brand.name}. Pick a type, dimension and describe what you want.
      </p>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {QUICK_ACTIONS.map(({ title, prompt, Icon }) => (
          <button
            key={title}
            onClick={() => setInput(prompt)}
            className="group aspect-square rounded-xl border border-slate-200 bg-white hover:shadow-sm hover:border-slate-300 transition p-5 flex flex-col items-start justify-between text-left"
            data-testid={`creator-quick-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-slate-700" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">{title}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{prompt}</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );

  return (
    <>
      <ToolShell
        brand={brand}
        tool="creator"
        title="The Brand Creator"
        Icon={ImageIcon}
        sessionId={sessionId}
        onLoadSession={loadSession}
        onNewChat={newChat}
        refreshKey={refreshKey}
        emptyState={emptyState}
        isEmpty={isEmpty}
        composer={
          <>
            <div className="rounded-2xl shadow-lg border border-slate-200 bg-white p-2">
              <ParamRow
                label="Type"
                options={TYPES}
                value={assetType}
                onChange={setAssetType}
                testIdPrefix="creator-type"
              />
              <ParamRow
                label="Size"
                options={RATIOS}
                value={ratio}
                onChange={setRatio}
                testIdPrefix="creator-ratio"
              />
              {ratio === "Other" && (
                <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0 w-16">Custom</span>
                  <input
                    data-testid="creator-width"
                    type="number"
                    min={64}
                    max={4096}
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="W"
                    className="w-20 h-7 px-2 rounded-md border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900"
                  />
                  <span className="text-xs text-slate-400">×</span>
                  <input
                    data-testid="creator-height"
                    type="number"
                    min={64}
                    max={4096}
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="H"
                    className="w-20 h-7 px-2 rounded-md border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900"
                  />
                  <span className="text-[11px] text-slate-400 ml-2">px</span>
                </div>
              )}
              <Composer
                value={input}
                onChange={setInput}
                onSend={send}
                busy={busy}
                placeholder="Describe the visual you want…"
              />
            </div>
            <div className="text-[11px] text-slate-400 text-center mt-2">
              {busy ? "Beeld wordt gegenereerd…" : "Creator volgt alleen Merksysteem, Kleuren en Typografie als bron."}
            </div>
          </>
        }
      >
        {messages.map((m) => (
          <ChatBubble key={m.id || m.created_at} role={m.role} streaming={m.streaming && !m.images?.length}>
            {m.role === "user" ? (
              <>
                {(m.asset_type || m.aspect) && (
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1 flex gap-2">
                    {m.asset_type && m.asset_type !== "—" && <span>{m.asset_type}</span>}
                    {m.aspect && m.aspect !== "—" && <span>· {m.aspect}</span>}
                  </div>
                )}
                <UserText>{m.content}</UserText>
              </>
            ) : (
              <>
                {m.streaming && !m.images?.length && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Sparkles className="w-4 h-4 animate-pulse" /> Visual aan het schetsen…
                  </div>
                )}
                {m.content && <AssistantMarkdown content={m.content} />}
                {m.images?.length > 0 && (
                  <div className={`grid gap-3 mt-3 ${m.images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {m.images.map((img, i) => {
                      const items = m.images.map((x) => ({
                        src: fileUrl(x.file_id),
                        downloadUrl: fileUrl(x.file_id, { download: true }),
                        alt: x.filename || "Generated",
                      }));
                      return (
                        <button
                          key={img.file_id}
                          onClick={() => setLightbox({ items, index: i })}
                          data-testid={`creator-thumb-${img.file_id}`}
                          className="group block rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-slate-900 transition relative"
                        >
                          <img
                            src={fileUrl(img.file_id)}
                            alt="Generated"
                            className="w-full aspect-square object-cover group-hover:opacity-90 transition"
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition">
                            Open preview
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </ChatBubble>
        ))}
      </ToolShell>
      <ImageLightbox
        items={lightbox?.items}
        index={lightbox?.index ?? 0}
        onIndexChange={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
        onClose={() => setLightbox(null)}
      />
    </>
  );
}

function ParamRow({ label, options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 px-2 pt-1.5 pb-1 border-b border-slate-100">
      <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0 w-16">{label}</span>
      <div className="flex flex-nowrap gap-1.5 overflow-x-auto bh-scroll pr-2">
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(active ? "" : o.value)}
              data-testid={`${testIdPrefix}-${o.value.toLowerCase().replace(/[\s:×]+/g, "-")}`}
              className={`shrink-0 h-7 px-2.5 rounded-full text-xs font-medium border transition ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
